import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { fetchTestimonials } from '../services/api';

// Real, approved testimonials only. Renders NOTHING when there are none -
// no placeholders, no fabricated quotes, no forever-skeleton. Public-safe:
// fetchTestimonials is an unauthenticated read, so this works on the
// signed-out landing page.
//
// One quote at a time, rotating (founder call 2026-07-26, after trying a wall:
// six or seven cards at once is too heavy for a landing page).
//
// Two things keep the rotation honest, and they are load-bearing:
//   1. The dots show the REAL number of quotes. A loop with no count invites a
//      visitor to assume there are more than there are - and the moment they
//      notice it cycling, they discount the sourced numbers directly above it.
//      One dot for one quote is the honest version of this pattern.
//   2. It does not auto-advance for readers who asked their OS for reduced
//      motion. They still get every quote, via the arrows and dots.

// How long a quote stays up: as long as it takes to read (founder, 2026-09-13:
// "longer reviews need more time to read"). A floor for the shortest, about a
// quarter of a second a word - a reading pace near 250 words a minute - and a
// ceiling so a very long one still moves. Exported so the test pins the numbers.
const MIN_MS = 3500;
const PER_WORD_MS = 240;
const MAX_MS = 20000;
export const dwellMs = (quote) => {
    const words = String(quote || '').trim().split(/\s+/).filter(Boolean).length;
    return Math.min(MAX_MS, MIN_MS + words * PER_WORD_MS);
};

const prefersReducedMotion = () =>
    typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const realQuotes = (list) => (Array.isArray(list) ? list.filter((t) => t?.quote) : []);

// `items`: a host page that already fetched the quotes passes them in, so the
// page makes one request, not two. Omitted, the carousel fetches its own, as
// it always has.
//
// `reserveHeight`: every quote is laid into the same grid cell and only the
// current one is visible, so the block is always as tall as its tallest
// quote. Without it the block resizes on every rotation and whatever sits
// below it jumps while someone is reading.
//
// `compact`: the landing page's hero carousel (founder, 2026-09-29: "why do we
// have testimonials in 2 places.. we can do the carousel in the top itself").
// Smaller type; a quote longer than `clampChars` shows its first
// `clampLines` lines with a "Read more" that expands it in place (a reader's
// choice, the only thing that changes the block's height); and the arrows sit
// beside the dots under the card with 44px targets, never over the words.
//
// Rotation pauses while the pointer is over the block, while anything in it
// has keyboard focus, and while a quote is expanded, and never runs for a
// reader who asked their OS for reduced motion (WCAG 2.2.2).
const TestimonialCarousel = ({ items, reserveHeight = false, compact = false, clampLines = 3, clampChars = 160 }) => {
    const [fetched, setFetched] = useState([]);
    const [index, setIndex] = useState(0);
    const [hovered, setHovered] = useState(false);
    const [focused, setFocused] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const hosted = items !== undefined;
    // Memoized: a new array per render would restart the dwell timer every
    // time the host re-renders.
    const testimonials = useMemo(() => (hosted ? realQuotes(items) : fetched), [hosted, items, fetched]);
    const paused = hovered || focused || expanded;

    useEffect(() => {
        if (hosted) return undefined;
        let alive = true;
        fetchTestimonials().then((list) => {
            if (alive) setFetched(realQuotes(list));
        });
        return () => { alive = false; };
    }, [hosted]);

    // Auto-advance only when there is something to rotate through, nobody is
    // reading it (hover, focus, an expanded quote) AND the reader hasn't asked
    // for less motion. One timeout per quote, its length from the words on
    // screen; it restarts whenever the quote changes, so a reader's own step
    // (arrow or dot) gives the quote they chose its full time.
    useEffect(() => {
        if (testimonials.length < 2 || paused || prefersReducedMotion()) return undefined;
        const current = testimonials[Math.min(index, testimonials.length - 1)];
        const timer = setTimeout(
            () => setIndex((i) => (i + 1) % testimonials.length),
            dwellMs(current?.quote)
        );
        return () => clearTimeout(timer);
    }, [testimonials, index, paused]);

    // Honest by construction: no approved quotes → no section at all.
    if (testimonials.length === 0) return null;

    const currentIndex = Math.min(index, testimonials.length - 1);
    const current = testimonials[currentIndex];
    const go = (i) => { setExpanded(false); setIndex((i + testimonials.length) % testimonials.length); };
    const long = (t) => compact && String(t.quote).length > clampChars;

    const figure = (t, { hidden = false, style, i = 0 } = {}) => {
        const clamped = long(t) && !(expanded && !hidden);
        return (
            <figure
                key={t.id || `${t.display_name}-${i}`}
                aria-hidden={hidden || undefined}
                style={style}
                className={`bg-white rounded-2xl border border-[#E0DED8] ${compact ? 'px-4 pb-1 pt-4 text-left sm:px-5' : 'shadow-sm px-8 py-8 sm:px-12 text-center'} ${hidden ? 'invisible' : ''}`}
            >
                <blockquote
                    className={`font-serif text-[#2C2C2C] ${compact ? 'text-base italic leading-relaxed' : 'text-xl sm:text-2xl leading-relaxed'}`}
                    style={clamped ? { display: '-webkit-box', WebkitLineClamp: clampLines, WebkitBoxOrient: 'vertical', overflow: 'hidden' } : undefined}
                >
                    &quot;{t.quote}&quot;
                </blockquote>
                <figcaption className={`${compact ? 'mt-1 justify-start text-sm' : 'mt-4 justify-center text-sm'} font-semibold text-[#1A4D2E] flex flex-wrap items-center gap-x-2 gap-y-1`}>
                    <span>{`${t.display_name}${t.role ? `, ${t.role}` : ''}`}</span>
                    {/* A fact about the account, read live by the server (a lapsed
                        pass drops it); never a badge a quote can be edited into. */}
                    {t.season_pass === true && (
                        <span
                            data-testid={hidden ? undefined : 'season-pass-mark'}
                            className="inline-flex items-center rounded-full border border-[#A8C5A6] bg-[#F0F7F1] px-2 py-0.5 text-xs font-medium text-[#1A4D2E]"
                        >
                            Season Pass
                        </span>
                    )}
                    {/* In the attribution row, so the control costs no extra line. */}
                    {long(t) && !hidden && (
                        <button
                            type="button"
                            onClick={() => setExpanded((e) => !e)}
                            aria-expanded={expanded}
                            className="ml-auto inline-flex min-h-[44px] items-center text-sm font-semibold text-[#1A4D2E] underline decoration-[#CFE3D4] underline-offset-4"
                        >
                            {expanded ? 'Show less' : 'Read more'}
                        </button>
                    )}
                </figcaption>
            </figure>
        );
    };

    const arrowCls = compact
        ? 'inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#E0DED8] bg-white text-[#1A4D2E] hover:bg-[#F0F7F1] transition-colors'
        : null;
    const prev = (
        <button type="button" onClick={() => go(currentIndex - 1)} aria-label="Previous testimonial"
                className={arrowCls || `absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 sm:-translate-x-5
                    bg-white border border-[#E0DED8] rounded-full p-1.5 text-[#1A4D2E]
                    hover:bg-[#F0F7F1] transition-colors shadow-sm`}>
            <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
        </button>
    );
    const next = (
        <button type="button" onClick={() => go(currentIndex + 1)} aria-label="Next testimonial"
                className={arrowCls || `absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 sm:translate-x-5
                    bg-white border border-[#E0DED8] rounded-full p-1.5 text-[#1A4D2E]
                    hover:bg-[#F0F7F1] transition-colors shadow-sm`}>
            <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
        </button>
    );
    // One dot per real quote - the count is never overstated.
    const dots = testimonials.map((t, i) => (
        <button
            type="button"
            key={t.id || `${t.display_name}-${i}`}
            onClick={() => go(i)}
            aria-label={`Go to testimonial ${i + 1} of ${testimonials.length}`}
            aria-current={i === currentIndex}
            className={compact ? 'inline-flex h-8 w-6 items-center justify-center' : `h-2 w-2 rounded-full transition-colors ${i === currentIndex
                ? 'bg-[#1A4D2E]'
                : 'bg-[#D6D2C6] hover:bg-[#A8C5A6]'}`}
        >
            {compact && <span aria-hidden="true" className={`h-2 w-2 rounded-full ${i === currentIndex ? 'bg-[#1A4D2E]' : 'bg-[#D6D2C6]'}`} />}
        </button>
    ));

    return (
        <div
            className="max-w-3xl mx-auto"
            data-testid="testimonial-carousel"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            // Keyboard focus pauses; a mouse click on an arrow (which also
            // focuses it in Chrome and Firefox) does not freeze rotation.
            onFocus={(e) => { let kb = true; try { kb = e.target.matches(':focus-visible'); } catch { kb = true; } if (kb) setFocused(true); }}
            onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setFocused(false); }}
        >
            <div className="relative">
                {reserveHeight ? (
                    <div className="grid" data-testid="testimonial-stack">
                        {testimonials.map((t, i) => figure(t, {
                            i,
                            hidden: i !== currentIndex,
                            // Centered in the reserved height, so a short quote
                            // does not sit on top of empty card.
                            style: { gridArea: '1 / 1', display: 'flex', flexDirection: 'column', justifyContent: 'center' },
                        }))}
                    </div>
                ) : figure(current)}

                {testimonials.length > 1 && !compact && <>{prev}{next}</>}
            </div>

            {testimonials.length > 1 && (
                compact ? (
                    <div className="mt-2 flex items-center justify-center gap-1">{prev}{dots}{next}</div>
                ) : (
                    <div className="flex items-center justify-center gap-2 mt-4">{dots}</div>
                )
            )}
        </div>
    );
};

export default TestimonialCarousel;
